import {
  CACHE_KEYS,
  getDB,
  getFromCache,
  saveToCache,
  isCacheExpired,
  updateCacheTime,
  clearCacheAndTime,
  isSafariOrIOS,
} from '@/utils/CacheHelper'
import { useEndpoints } from '@/composables'
import { httpClient } from '@/services'
import { getGeoObject } from '@/store/modules/live-map/live-map.helpers'

const STORE_NAME = CACHE_KEYS.STORE.PRIORITY_ZONES
const CACHE_TIME_KEY = CACHE_KEYS.TIME.PRIORITY_ZONES
const API_URL = useEndpoints.geoFence.priorityZone.index()

export async function getAllPriorityZoneData(
  cacheTime = CACHE_KEYS.EXPIRE_TIME.THREE_DAYS
) {
  // ✅ Safari: bypass IndexedDB entirely, fetch all pages directly
  if (isSafariOrIOS()) {
    let totalCount = Infinity
    let offset = 0
    const all = []

    const fetchNextBatchDirect = async () => {
      const res = await httpClient
        .get(API_URL, { params: { offset, limit: 500 } })
        .then((res) => res)
        .catch((err) => {
          console.log('priority-zone-fetch-err (Safari)', { err })
          return { data: { meta: { count: { total: 0 } }, data: [] } }
        })

      const batch = res.data.data
      totalCount = res.data.meta.count.total

      const modifiedBatch = batch.map((item) => ({
        ...item,
        isSelected: false,
        geo_center_coords: getGeoObject(item.center_coords),
      }))

      all.push(...modifiedBatch)
      offset += batch.length
      console.log(`Fetched-Priority Zone (Safari) ${offset}/${totalCount}`)

      if (offset < totalCount) {
        await new Promise((r) => setTimeout(r, 100)) // small delay
        return fetchNextBatchDirect()
      }
      return all
    }

    return await fetchNextBatchDirect()
  }

  // 🔁 Non-Safari: original behavior (cache-first)
  if (!isCacheExpired(CACHE_TIME_KEY, cacheTime)) {
    return await getFromCache(STORE_NAME)
  }

  await clearCacheAndTime(STORE_NAME, CACHE_TIME_KEY)

  let totalCount = Infinity
  let offset = 0

  async function fetchNextBatch() {
    const res = await httpClient
      .get(API_URL, {
        params: { offset, limit: 500 },
      })
      .then((res) => res)
      .catch((err) => {
        console.log('priority-zone-fetch-err', { err })
        return { data: { meta: { count: { total: 0 } }, data: [] } }
      })

    const batch = res.data.data
    totalCount = res.data.meta.count.total

    const modifiedBatch = batch.map((item) => {
      return {
        ...item,
        isSelected: false,
        geo_center_coords: getGeoObject(item.center_coords),
        is_active: item.is_active ? 1 : 0, // true for 1, false for 0
      }
    })

    await saveToCache(STORE_NAME, modifiedBatch)

    offset += batch.length
    console.log(`Fetched-Priority Zone ${offset}/${totalCount}`)

    if (offset < totalCount) {
      // Optional: delay between batches to avoid stress on server
      await new Promise((resolve) => setTimeout(resolve, 100)) // 100ms delay
      return await fetchNextBatch()
    }
    updateCacheTime(CACHE_TIME_KEY)
    return await getFromCache(STORE_NAME)
  }

  return await fetchNextBatch()
}

export async function getActivePriorityZoneData() {
  // ✅ Safari: fetch direct, filter active, no IndexedDB
  if (isSafariOrIOS()) {
    let totalCount = Infinity
    let offset = 0
    const active = []

    const fetchNextBatchDirect = async () => {
      const res = await httpClient
        .get(API_URL, { params: { offset, limit: 500 } })
        .then((res) => res)
        .catch((err) => {
          console.log('priority-zone-fetch-err', { err })
          return { data: { meta: { count: { total: 0 } }, data: [] } }
        })

      const batch = res.data.data
      totalCount = res.data.meta.count.total

      const modifiedBatch = batch.map((item) => ({
        ...item,
        isSelected: false,
        geo_center_coords: getGeoObject(item.center_coords),
      }))

      active.push(...modifiedBatch.filter((i) => i.is_active))
      offset += batch.length
      console.log(
        `Fetched-Active Priority Zone (Safari) ${offset}/${totalCount}`
      )

      if (offset < totalCount) {
        await new Promise((r) => setTimeout(r, 100))
        return fetchNextBatchDirect()
      }
      return active
    }

    return await fetchNextBatchDirect()
  }

  // 🔁 Non-Safari: original behavior
  // ✅ 1. Return only active areas from  cache if not expired
  if (!isCacheExpired(CACHE_TIME_KEY)) {
    const db = await getDB()
    const tx = db.transaction(STORE_NAME, 'readonly')
    const store = tx.objectStore(STORE_NAME)

    if (store.indexNames.contains('is_active')) {
      const results = await store.index('is_active').getAll(1)
      return results
    } else {
      // console.warn(`Index "is_active" not found on store "${STORE_NAME}"`)
      return await store.getAll()
    }
  }

  // ✅ 2. Cache expired → clear old data
  await clearCacheAndTime(STORE_NAME, CACHE_TIME_KEY)

  // ✅ 3. Fetch fresh data from API
  let totalCount = Infinity
  let offset = 0

  async function fetchNextBatch() {
    const res = await httpClient
      .get(API_URL, {
        params: { offset, limit: 500 },
      })
      .then((res) => res)
      .catch((err) => {
        console.log('priority-zone-fetch-err', { err })
        return { data: { meta: { count: { total: 0 } }, data: [] } }
      })

    const batch = res.data.data
    totalCount = res.data.meta.count.total

    const modifiedBatch = batch.map((item) => {
      return {
        ...item,
        isSelected: false,
        geo_center_coords: getGeoObject(item.center_coords),
        is_active: item.is_active ? 1 : 0, // true for 1, false for 0
      }
    })
    // ✅ 4. Save modified batch to cache
    await saveToCache(STORE_NAME, modifiedBatch)

    offset += batch.length
    console.log(`Fetched-Priority Zone ${offset}/${totalCount}`)

    if (offset < totalCount) {
      await new Promise((resolve) => setTimeout(resolve, 100))
      return await fetchNextBatch()
    }

    // ✅ 4. Update cache time after successful fetch
    updateCacheTime(CACHE_TIME_KEY)

    // ✅ 5. Return only active service areas from fresh cache
    const db = await getDB()
    const tx = db.transaction(STORE_NAME, 'readonly')
    const store = tx.objectStore(STORE_NAME)

    if (store.indexNames.contains('is_active')) {
      const results = await store.index('is_active').getAll(1)
      return results
    } else {
      // console.warn(`Index "is_active" not found on store "${STORE_NAME}"`)
      return await store.getAll()
    }
  }

  return await fetchNextBatch()
}
